import { lazy } from 'react';

/**
 * Wraps React.lazy() with a single automatic reload on chunk-load failures.
 *
 * When the dynamic import fails (e.g. after a deploy that invalidates old
 * chunk hashes) and the page has not yet been force-refreshed, we store a
 * flag in localStorage and trigger a full reload.  The reload is triggered
 * via a separate microtask *after* the flag is set; the function then throws
 * so that React's lazy() does not try to use an undefined module value.
 *
 * If the page has already been reloaded once, we let the error propagate so
 * React's error boundary can handle it gracefully.
 */
const lazyWithRetry = (componentImport: () => Promise<any>): any =>
    lazy(async () => {
        const flagKey = 'page-has-been-force-refreshed';
        const pageHasAlreadyBeenForceRefreshed = JSON.parse(
            window.localStorage.getItem(flagKey) || 'false'
        );

        try {
            const component = await componentImport();
            window.localStorage.setItem(flagKey, 'false');
            return component;
        } catch (error) {
            if (!pageHasAlreadyBeenForceRefreshed) {
                // Assume the user has a stale chunk. Reload once to fetch fresh assets.
                window.localStorage.setItem(flagKey, 'true');
                // Schedule the reload after this microtask so the flag is persisted first.
                // Then throw so lazy() does not receive undefined.
                setTimeout(() => window.location.reload(), 0);
                throw error;
            }

            // Already reloaded – let the error propagate to the error boundary.
            throw error;
        }
    });

export const HomePage = lazyWithRetry(() =>
    import('../app/pages/Home').then((module) => ({
        default: module.Home,
    }))
);

export const LegalComponent = lazyWithRetry(() =>
    import('../app/pages/Legal').then((module) => ({
        default: module.Legal,
    }))
);
export const HelpComponent = lazyWithRetry(() =>
    import('../app/pages/Help').then((module) => ({
        default: module.Help,
    }))
);
export const CustomersComponent = lazyWithRetry(() =>
    import('../app/pages/Customers/index').then((module) => ({
        default: module.Customers,
    }))
);

export const CustomerDetailComponent = lazyWithRetry(() =>
    import('../app/pages/CustomerDetail/index').then((module) => ({
        default: module.CustomerDetail,
    }))
);

export const SiteDetailComponent = lazyWithRetry(() =>
    import('../app/pages/SiteDetail/index').then((module) => ({
        default: module.SiteDetail,
    }))
);

export const FilterDetailComponent = lazyWithRetry(() =>
    import('../app/pages/FilterDetail/index').then((module) => ({
        default: module.FilterDetail,
    }))
);
